import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import api, { API_BASE } from '../services/api';
import { PillButton, Input, SegmentedControl, Toggle } from '../ui';
import LanguageSelector from '../components/LanguageSelector';
import { getTrustTokensToSend, saveTrustToken } from '../utils/trustTokens';
import { ReactComponent as MailIcon } from '../assets/icons/mail.svg';
import { ReactComponent as CreditCardIcon } from '../assets/icons/credit-card.svg';
import { ReactComponent as KeyIcon } from '../assets/icons/key-round.svg';
import { ReactComponent as LockIcon } from '../assets/icons/lock.svg';
import { ReactComponent as EyeIcon } from '../assets/icons/eye.svg';
import { ReactComponent as EyeOffIcon } from '../assets/icons/eye-off.svg';
import { ReactComponent as ShieldIcon } from '../assets/icons/shield.svg';
import { ReactComponent as NfcIcon } from '../assets/icons/nfc.svg';
import { ReactComponent as ClockIcon } from '../assets/icons/clock.svg';
import { ReactComponent as AlertTriangleIcon } from '../assets/icons/alert-triangle.svg';
import { ReactComponent as AlertCircleIcon } from '../assets/icons/alert-circle.svg';
import { ReactComponent as ChevronLeftIcon } from '../assets/icons/chevron-left.svg';
import './Login.css';
import { apiErrorMessage, apiErrorFromData } from '../utils/apiError';
import { withLanguage } from '../i18n/language';

// { método interno -> valor que usa branding.loginMethods en el backend }
// 'nfc' no aparece: web NFC vive dentro del método 'card' (es solo un modo de
// leer la misma tarjeta), no es un método de login aparte.
const METHOD_TO_BRANDING_KEY = { email: 'email', card: 'card', master: 'master_password' };

// Pasos del método "Card". "scan" es el punto de partida siempre; el resto
// son las respuestas de POST /api/auth/login descritas en
// design/CARD-LOGIN-2FA.md.
const CARD_STEPS = {
  SCAN: 'scan',
  SECOND_FACTOR: 'second-factor',
  TOO_MANY: 'too-many',
  REGISTER: 'register',
  APPROVAL_PENDING: 'approval-pending',
  SETUP_NEEDED: 'setup-needed',
};

function formatCountdown(totalSeconds) {
  const s = Math.max(0, totalSeconds);
  const m = Math.floor(s / 60);
  const rest = s % 60;
  return `${m}:${String(rest).padStart(2, '0')}`;
}

function LoginV2({ onLoginSuccess, allowedMethods }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [method, setMethod] = useState('email');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // ?register=1&plan=team|business — venido de la landing ("crear cuenta con
  // este plan"). Abre el registro directamente en vez del login, y guarda el
  // plan para preseleccionarlo en el asistente de crear organización tras
  // registrarse (ver App.js handleLoginSuccess y AdminLayout).
  const [isRegister, setIsRegister] = useState(searchParams.get('register') === '1');
  useEffect(() => {
    const plan = searchParams.get('plan');
    if (plan === 'team' || plan === 'business') {
      sessionStorage.setItem('pendingPlan', plan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [cardId, setCardId] = useState('');
  const [rememberDevice, setRememberDevice] = useState(true);

  // --- Estado del flujo de tarjeta (design/CARD-LOGIN-2FA.md) ---
  const [cardStep, setCardStep] = useState(CARD_STEPS.SCAN);
  const [pendingRfid, setPendingRfid] = useState(''); // identificador ya resuelto por el servidor, en espera de factor
  const [secondFactorMethods, setSecondFactorMethods] = useState([]); // ['pin', 'master_password']
  const [secondFactorMethod, setSecondFactorMethod] = useState('pin');
  const [factorPin, setFactorPin] = useState('');
  const [factorMasterPassword, setFactorMasterPassword] = useState('');
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [retryAfter, setRetryAfter] = useState(0); // segundos, cuenta atrás en pantalla
  const [approvalId, setApprovalId] = useState('');
  const [registerPin, setRegisterPin] = useState('');
  const [registerPinConfirm, setRegisterPinConfirm] = useState('');
  const [nfcScanning, setNfcScanning] = useState(false);
  const nfcReaderRef = useRef(null);

  const [masterRfidId, setMasterRfidId] = useState('');
  const [masterPassword, setMasterPassword] = useState('');

  const methodOptions = [
    { value: 'email', label: t('loginV2.methodEmail'), icon: MailIcon },
    { value: 'card', label: t('loginV2.methodCard'), icon: CreditCardIcon },
    { value: 'master', label: t('loginV2.methodMaster'), icon: KeyIcon },
  ].filter((opt) => !allowedMethods || allowedMethods.includes(METHOD_TO_BRANDING_KEY[opt.value]));

  useEffect(() => {
    if (methodOptions.length && !methodOptions.some((m) => m.value === method)) {
      setMethod(methodOptions[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedMethods]);

  const handleAuthSuccess = useCallback((data, rfidOverride) => {
    const rfid = rfidOverride || data.card?.rfidId || data.rfidId;
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('rfidId', rfid);
    localStorage.setItem('expiresAt', data.expiresAt);
    localStorage.setItem('sessionTimeout', data.sessionConfig?.timeout || '24h');
    axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
    if (data.trustToken) saveTrustToken(data.trustToken);
    onLoginSuccess(data);
  }, [onLoginSuccess]);

  const resetCardFlow = useCallback(() => {
    setCardStep(CARD_STEPS.SCAN);
    setPendingRfid('');
    setSecondFactorMethods([]);
    setFactorPin('');
    setFactorMasterPassword('');
    setAttemptsRemaining(null);
    setRetryAfter(0);
    setApprovalId('');
    setRegisterPin('');
    setRegisterPinConfirm('');
    setError('');
  }, []);

  // Salir del método "Card" deja el flujo listo para la próxima vez que se entre.
  useEffect(() => {
    if (method !== 'card') resetCardFlow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method]);

  // Cuenta atrás de too_many_attempts. Al llegar a 0 se puede reintentar.
  useEffect(() => {
    if (cardStep !== CARD_STEPS.TOO_MANY || retryAfter <= 0) return undefined;
    const id = setInterval(() => setRetryAfter((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [cardStep, retryAfter]);

  // ⌨️ Tarjeta / lector RFID por USB: escritura tipo teclado + Enter.
  // Igual que components/Login.js, para no romper el flujo de los lectores ya
  // desplegados. Solo activo en el paso "scan": en los demás pasos el foco
  // está en un campo de PIN/contraseña y el propio input recibe la tecla.
  useEffect(() => {
    if (method !== 'card' || cardStep !== CARD_STEPS.SCAN) return undefined;
    let buffer = '';
    let timeout = null;
    const handleKeyPress = (e) => {
      if (e.target.tagName === 'INPUT') return;
      if (e.key === 'Enter' && buffer.length > 0) {
        const scanned = buffer.trim();
        buffer = '';
        setCardId(scanned);
        handleCardLogin(scanned);
      } else if (e.key.length === 1) {
        buffer += e.key;
        clearTimeout(timeout);
        timeout = setTimeout(() => { buffer = ''; }, 1000);
      }
    };
    window.addEventListener('keypress', handleKeyPress);
    return () => { window.removeEventListener('keypress', handleKeyPress); clearTimeout(timeout); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method, cardStep]);

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (isRegister) return handleRegister(e);
    if (!email || !password) { setError(t('loginV2.emailRequired')); return; }
    setError(''); setLoading(true);
    try {
      const res = await api.post('/account/login', withLanguage({ email, password }));
      if (res.data.success) handleAuthSuccess(res.data);
    } catch (err) {
      setError(apiErrorMessage(err, t, 'loginV2.error'));
    } finally { setLoading(false); }
  };

  // Aplica la respuesta de un intento de login por tarjeta (primer envío o
  // reenvío con PIN/contraseña maestra): mueve cardStep según el `code`.
  // `data` es el body de la respuesta tal cual, venga de un throw (4xx/429)
  // o de una respuesta 2xx sin `success` (202 approval_pending, 200 con
  // mustSetFactor no es este caso: ese sí trae success:true y es login).
  const applyCardCode = useCallback((data, id) => {
    switch (data.code) {
      case 'card_not_registered':
        setPendingRfid(id);
        setCardStep(CARD_STEPS.REGISTER);
        setError('');
        break;
      case 'card_inactive':
        setError(t('loginV2.cardInactive'));
        break;
      case 'second_factor_required':
        setPendingRfid(id);
        setSecondFactorMethods(data.methods || []);
        setSecondFactorMethod((data.methods || [])[0] === 'master_password' ? 'master_password' : 'pin');
        setAttemptsRemaining(null);
        setCardStep(CARD_STEPS.SECOND_FACTOR);
        setError('');
        break;
      case 'second_factor_invalid':
        setAttemptsRemaining(data.remaining);
        setError('');
        break;
      case 'too_many_attempts':
        setRetryAfter(data.retryAfter || 0);
        setCardStep(CARD_STEPS.TOO_MANY);
        setError('');
        break;
      case 'approval_pending':
        setPendingRfid(id);
        setApprovalId(data.approvalId || '');
        setCardStep(CARD_STEPS.APPROVAL_PENDING);
        setError('');
        break;
      case 'second_factor_setup_needed':
        setCardStep(CARD_STEPS.SETUP_NEEDED);
        setError('');
        break;
      default:
        setError(apiErrorFromData(data, t, 'loginV2.error'));
    }
  }, [t]);

  const handleCardLogin = async (id = cardId) => {
    if (!id) { setError(t('loginV2.cardRequired')); return; }
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/api/auth/login`, withLanguage({
        rfidId: id,
        trustTokens: getTrustTokensToSend(),
        rememberDevice,
      }));
      // 202 approval_pending (y cualquier otro código de la tabla que venga
      // en un 2xx) no lanza: axios solo lanza por status fuera de 2xx.
      if (res.data.success) handleAuthSuccess(res.data, id);
      else applyCardCode(res.data || {}, id);
    } catch (err) {
      applyCardCode(err.response?.data || {}, id);
    } finally { setLoading(false); }
  };

  // Reenvío con PIN o contraseña maestra tras second_factor_required /
  // second_factor_invalid.
  const handleSecondFactorSubmit = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const body = withLanguage({
        rfidId: pendingRfid,
        trustTokens: getTrustTokensToSend(),
        rememberDevice,
      });
      if (secondFactorMethod === 'master_password') body.masterPassword = factorMasterPassword;
      else body.pin = factorPin;
      const res = await axios.post(`${API_BASE}/api/auth/login`, body);
      if (res.data.success) handleAuthSuccess(res.data, pendingRfid);
      else applyCardCode(res.data || {}, pendingRfid);
    } catch (err) {
      applyCardCode(err.response?.data || {}, pendingRfid);
    } finally { setLoading(false); }
  };

  const handleRegisterCard = async (e) => {
    e.preventDefault();
    if (registerPin.length < 6 || registerPin.length > 12 || !/^\d+$/.test(registerPin)) {
      setError(t('loginV2.pinLength')); return;
    }
    if (registerPin !== registerPinConfirm) { setError(t('loginV2.pinMismatch')); return; }
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/api/auth/register-card`, withLanguage({
        identifier: pendingRfid,
        type: 'card',
        pin: registerPin,
        rememberDevice,
      }));
      if (res.data.success) handleAuthSuccess(res.data, pendingRfid);
    } catch (err) {
      setError(apiErrorMessage(err, t, 'loginV2.error'));
    } finally { setLoading(false); }
  };

  // Web NFC (Chrome Android): lee el UID de la tarjeta acercándola al
  // teléfono, en vez de escribirlo. Solo se ofrece si el navegador lo soporta.
  const handleNfcScan = async () => {
    setError(''); setNfcScanning(true);
    try {
      // eslint-disable-next-line no-undef
      const reader = new NDEFReader();
      nfcReaderRef.current = reader;
      await reader.scan();
      reader.onreading = (event) => {
        const serial = (event.serialNumber || '').replace(/:/g, '').toUpperCase();
        setNfcScanning(false);
        if (serial) { setCardId(serial); handleCardLogin(serial); }
        else setError(t('loginV2.nfcError'));
      };
      reader.onreadingerror = () => { setNfcScanning(false); setError(t('loginV2.nfcError')); };
    } catch {
      setNfcScanning(false);
      setError(t('loginV2.nfcError'));
    }
  };

  const handleMasterLogin = async (e) => {
    e.preventDefault();
    if (!masterRfidId || !masterPassword) { setError(t('loginV2.masterRequired')); return; }
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/api/devices/auth/master-password`, {
        rfidId: masterRfidId.trim(),
        masterPassword,
      });
      if (res.data.success) handleAuthSuccess(res.data, masterRfidId.trim());
    } catch (err) {
      setError(apiErrorMessage(err, t, 'loginV2.error'));
    } finally { setLoading(false); }
  };

  const handleForgotPassword = async () => {
    if (!email) { setError(t('loginV2.emailRequired')); return; }
    try {
      await api.post('/account/forgot-password', { email });
      setError('');
      window.alert(t('loginV2.forgotPasswordSent'));
    } catch {
      window.alert(t('loginV2.forgotPasswordSent'));
    }
  };

  const handleRegister = async (e) => {
    e?.preventDefault?.();
    if (!email || !password) { setError(t('loginV2.emailRequired')); return; }
    setError(''); setLoading(true);
    try {
      const res = await api.post('/account/register', withLanguage({ email, password }));
      if (res.data.success) handleAuthSuccess(res.data);
    } catch (err) {
      setError(apiErrorMessage(err, t, 'loginV2.error'));
    } finally { setLoading(false); }
  };

  return (
    <div className="sgc-login">
      <div className="sgc-login__lang">
        <LanguageSelector />
      </div>

      <div className="sgc-login__box">
        <div className="sgc-login__hero">
          <ShieldIcon width={32} height={32} aria-hidden="true" />
          <h1>{isRegister ? t('loginV2.registerHero') : t('loginV2.hero')}</h1>
          <p>{isRegister ? t('loginV2.registerSubtitle') : t('loginV2.heroSubtitle')}</p>
        </div>

        {methodOptions.length > 1 && (method !== 'card' || cardStep === CARD_STEPS.SCAN) && (
          <SegmentedControl options={methodOptions} value={method} onChange={setMethod} className="sgc-login__segmented" />
        )}

        {error && <p className="sgc-login__error">{error}</p>}

        {method === 'email' && (
          <form className="sgc-login__form" onSubmit={handleEmailLogin}>
            <Input icon={MailIcon} label={t('loginV2.emailLabel')} type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
            <Input
              icon={LockIcon}
              label={t('loginV2.passwordLabel')}
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              rightAction={(
                <button type="button" onClick={() => setShowPassword((v) => !v)} className="sgc-login__eye" aria-label={t('loginV2.togglePassword')}>
                  {showPassword ? <EyeOffIcon width={18} height={18} aria-hidden="true" /> : <EyeIcon width={18} height={18} aria-hidden="true" />}
                </button>
              )}
            />
            <PillButton type="submit" variant="primary" disabled={loading} className="sgc-login__submit">
              {isRegister ? t('loginV2.createAccount') : t('loginV2.signIn')}
            </PillButton>
            <div className="sgc-login__links">
              {isRegister ? (
                <button type="button" onClick={() => setIsRegister(false)}>{t('loginV2.backToLogin')}</button>
              ) : (
                <>
                  <button type="button" onClick={handleForgotPassword}>{t('loginV2.forgotPassword')}</button>
                  <button type="button" onClick={() => setIsRegister(true)}>{t('loginV2.createAccount')}</button>
                </>
              )}
            </div>
          </form>
        )}

        {method === 'card' && cardStep === CARD_STEPS.SCAN && (
          <div className="sgc-login__form">
            <div className="sgc-login__scan-prompt">
              <CreditCardIcon width={40} height={40} aria-hidden="true" />
              <p>{t('loginV2.cardPrompt')}</p>
            </div>
            <Input
              icon={CreditCardIcon}
              label={t('loginV2.cardIdLabel')}
              value={cardId}
              onChange={(e) => setCardId(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCardLogin(); } }}
              autoFocus
            />
            <Toggle checked={rememberDevice} onChange={setRememberDevice} label={t('loginV2.rememberDevice')} />
            <PillButton variant="primary" disabled={loading} onClick={() => handleCardLogin()} className="sgc-login__submit">
              {t('loginV2.signIn')}
            </PillButton>
            {typeof window !== 'undefined' && 'NDEFReader' in window && (
              <PillButton
                variant="secondary"
                icon={NfcIcon}
                disabled={nfcScanning}
                onClick={handleNfcScan}
                className="sgc-login__submit"
              >
                {nfcScanning ? t('loginV2.nfcScanning') : t('loginV2.nfcTap')}
              </PillButton>
            )}
          </div>
        )}

        {method === 'card' && cardStep === CARD_STEPS.SECOND_FACTOR && (
          <form className="sgc-login__form" onSubmit={handleSecondFactorSubmit}>
            <div className="sgc-login__scan-prompt">
              <ShieldIcon width={32} height={32} aria-hidden="true" />
              <p>{t('loginV2.secondFactorSubtitle')}</p>
            </div>

            {secondFactorMethod === 'pin' ? (
              <Input
                icon={LockIcon}
                label={t('loginV2.pinLabel')}
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                maxLength={12}
                value={factorPin}
                onChange={(e) => setFactorPin(e.target.value.replace(/\D/g, ''))}
                autoFocus
              />
            ) : (
              <Input
                icon={KeyIcon}
                label={t('loginV2.masterPasswordLabel')}
                type={showPassword ? 'text' : 'password'}
                value={factorMasterPassword}
                onChange={(e) => setFactorMasterPassword(e.target.value)}
                autoFocus
                rightAction={(
                  <button type="button" onClick={() => setShowPassword((v) => !v)} className="sgc-login__eye" aria-label={t('loginV2.togglePassword')}>
                    {showPassword ? <EyeOffIcon width={18} height={18} aria-hidden="true" /> : <EyeIcon width={18} height={18} aria-hidden="true" />}
                  </button>
                )}
              />
            )}

            {attemptsRemaining !== null && (
              <p className="sgc-login__error">{t('loginV2.secondFactorInvalid', { count: attemptsRemaining })}</p>
            )}

            <Toggle checked={rememberDevice} onChange={setRememberDevice} label={t('loginV2.rememberDevice')} />

            <PillButton type="submit" variant="primary" disabled={loading} className="sgc-login__submit">
              {t('loginV2.signIn')}
            </PillButton>

            <div className="sgc-login__links">
              <button type="button" onClick={resetCardFlow}>
                <ChevronLeftIcon width={14} height={14} aria-hidden="true" style={{ verticalAlign: 'middle' }} />
                {' '}{t('loginV2.backToCard')}
              </button>
              {secondFactorMethods.length > 1 && (
                <button type="button" onClick={() => setSecondFactorMethod((m) => (m === 'pin' ? 'master_password' : 'pin'))}>
                  {secondFactorMethod === 'pin' ? t('loginV2.useMasterPasswordInstead') : t('loginV2.usePinInstead')}
                </button>
              )}
            </div>
          </form>
        )}

        {method === 'card' && cardStep === CARD_STEPS.TOO_MANY && (
          <div className="sgc-login__form">
            <div className="sgc-login__scan-prompt">
              <AlertTriangleIcon width={32} height={32} aria-hidden="true" />
              <p>{t('loginV2.tooManyAttemptsTitle')}</p>
              <p>{t('loginV2.tooManyAttemptsBody', { time: formatCountdown(retryAfter) })}</p>
            </div>
            <PillButton variant="secondary" disabled={retryAfter > 0} onClick={resetCardFlow} className="sgc-login__submit">
              <ClockIcon width={16} height={16} aria-hidden="true" /> {t('loginV2.backToCard')}
            </PillButton>
          </div>
        )}

        {method === 'card' && cardStep === CARD_STEPS.REGISTER && (
          <form className="sgc-login__form" onSubmit={handleRegisterCard}>
            <div className="sgc-login__scan-prompt">
              <CreditCardIcon width={32} height={32} aria-hidden="true" />
              <p>{t('loginV2.cardNotRegisteredTitle')}</p>
            </div>
            <Input
              icon={LockIcon}
              label={t('loginV2.setPinLabel')}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={12}
              value={registerPin}
              onChange={(e) => setRegisterPin(e.target.value.replace(/\D/g, ''))}
              autoFocus
            />
            <Input
              icon={LockIcon}
              label={t('loginV2.confirmPinLabel')}
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={12}
              value={registerPinConfirm}
              onChange={(e) => setRegisterPinConfirm(e.target.value.replace(/\D/g, ''))}
            />
            <Toggle checked={rememberDevice} onChange={setRememberDevice} label={t('loginV2.rememberDevice')} />
            <PillButton type="submit" variant="primary" disabled={loading} className="sgc-login__submit">
              {t('loginV2.createAccountWithCard')}
            </PillButton>
            <div className="sgc-login__links">
              <button type="button" onClick={resetCardFlow}>
                <ChevronLeftIcon width={14} height={14} aria-hidden="true" style={{ verticalAlign: 'middle' }} />
                {' '}{t('loginV2.backToCard')}
              </button>
            </div>
          </form>
        )}

        {method === 'card' && cardStep === CARD_STEPS.APPROVAL_PENDING && (
          <div className="sgc-login__form">
            <div className="sgc-login__scan-prompt">
              <MailIcon width={32} height={32} aria-hidden="true" />
              <p>{t('loginV2.approvalPendingTitle')}</p>
              <p>{t('loginV2.approvalPendingBody')}</p>
              {approvalId && <p className="sgc-login__approval-id">{approvalId}</p>}
            </div>
            <PillButton variant="primary" disabled={loading} onClick={() => handleCardLogin(pendingRfid)} className="sgc-login__submit">
              {t('loginV2.approvalPendingRetry')}
            </PillButton>
            <div className="sgc-login__links">
              <button type="button" onClick={resetCardFlow}>{t('loginV2.backToCard')}</button>
            </div>
          </div>
        )}

        {method === 'card' && cardStep === CARD_STEPS.SETUP_NEEDED && (
          <div className="sgc-login__form">
            <div className="sgc-login__scan-prompt">
              <AlertCircleIcon width={32} height={32} aria-hidden="true" />
              <p>{t('loginV2.setupNeededTitle')}</p>
              <p>{t('loginV2.setupNeededBody')}</p>
              <a href="mailto:info@nordicbyte.co.uk">{t('loginV2.setupNeededContact')}</a>
            </div>
            <div className="sgc-login__links">
              <button type="button" onClick={resetCardFlow}>{t('loginV2.backToCard')}</button>
            </div>
          </div>
        )}

        {method === 'master' && (
          <form className="sgc-login__form" onSubmit={handleMasterLogin}>
            <Input icon={CreditCardIcon} label={t('loginV2.cardIdLabel')} value={masterRfidId} onChange={(e) => setMasterRfidId(e.target.value)} />
            <Input
              icon={KeyIcon}
              label={t('loginV2.masterPasswordLabel')}
              type={showPassword ? 'text' : 'password'}
              value={masterPassword}
              onChange={(e) => setMasterPassword(e.target.value)}
              rightAction={(
                <button type="button" onClick={() => setShowPassword((v) => !v)} className="sgc-login__eye" aria-label={t('loginV2.togglePassword')}>
                  {showPassword ? <EyeOffIcon width={18} height={18} aria-hidden="true" /> : <EyeIcon width={18} height={18} aria-hidden="true" />}
                </button>
              )}
            />
            <PillButton type="submit" variant="primary" disabled={loading} className="sgc-login__submit">
              {t('loginV2.signIn')}
            </PillButton>
          </form>
        )}
      </div>
    </div>
  );
}

export default LoginV2;
